/**
 * Each agent's files and folders, and my conversations with it — loaded per
 * agent, when something on screen needs that agent.
 *
 * There is no project tree. A file is its agent's and a tree never spans
 * agents (docs/projects/index.md § What an agent reads), so the tree is
 * `GET /api/agents/{id}/files`. A conversation is mine, so it is read through
 * my own collection narrowed by the agent (`GET /api/conversations?agent_id=`),
 * never through a route under the agent.
 *
 * Both are cached by agent id, for the session. Nothing here is per project:
 * an agent's id is the whole key, and a member's cache holds only agents they
 * have access to, because the server answers nothing else.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import type { TreeItem } from '../types';
import { agentsApi, conversationsApi, type AgentFiles, type ConversationRow, type FileRow, type FolderRow } from '../services/api';
import { useFileStatusStore, type ProcessingStatus } from './fileStatusStore';
import { fileStem } from '../utils/filename';

/** Where a load stands. Absent means never asked. */
export type LoadState = 'loading' | 'ready' | 'failed';

interface AgentTreeState {
  /** Each agent's tree: folders and files, nested by folder. */
  trees: Record<string, TreeItem[]>;
  /** Each agent's conversations with me, flat, newest first. */
  conversations: Record<string, TreeItem[]>;
  treeLoad: Record<string, LoadState>;
  conversationLoad: Record<string, LoadState>;

  /** Load the agent's tree and conversations unless already here or on the way. */
  ensure: (agentId: string) => void;
  /** Read the agent's tree again. */
  refreshFiles: (agentId: string) => Promise<void>;
  /** Read the agent's conversations again. */
  refreshConversations: (agentId: string) => Promise<void>;
  /** Read again everything already loaded — after a change the screen did not make. */
  refreshLoaded: () => Promise<void>;
  /** Replace an agent's tree, or its conversations, through `update`. */
  setTree: (agentId: string, update: (items: TreeItem[]) => TreeItem[]) => void;
  setConversations: (agentId: string, update: (items: TreeItem[]) => TreeItem[]) => void;
  /** Forget everything — signing out. */
  reset: () => void;
}

// A file still being ingested is seeded into the processing stream once per
// agent: a later read is older than what the stream has said since.
const seeded = new Set<string>();

export const useAgentTreeStore = create<AgentTreeState>((set, get) => ({
  trees: {},
  conversations: {},
  treeLoad: {},
  conversationLoad: {},

  ensure: (agentId) => {
    const { treeLoad, conversationLoad } = get();
    if (!treeLoad[agentId] || treeLoad[agentId] === 'failed') void get().refreshFiles(agentId);
    if (!conversationLoad[agentId] || conversationLoad[agentId] === 'failed') void get().refreshConversations(agentId);
  },

  refreshFiles: async (agentId) => {
    set((s) => (s.treeLoad[agentId] === 'ready' ? s : { treeLoad: { ...s.treeLoad, [agentId]: 'loading' } }));
    try {
      const items = buildFileTree(await agentsApi.files(agentId));
      set((s) => ({
        trees: { ...s.trees, [agentId]: items },
        treeLoad: { ...s.treeLoad, [agentId]: 'ready' },
      }));
      if (!seeded.has(agentId)) {
        seeded.add(agentId);
        seedProcessing(items);
      }
    } catch (err) {
      console.error('Failed to load the agent\'s files:', err);
      set((s) => (s.treeLoad[agentId] === 'ready' ? s : { treeLoad: { ...s.treeLoad, [agentId]: 'failed' } }));
    }
  },

  refreshConversations: async (agentId) => {
    set((s) =>
      s.conversationLoad[agentId] === 'ready' ? s : { conversationLoad: { ...s.conversationLoad, [agentId]: 'loading' } },
    );
    try {
      const items = (await conversationsApi.forAgent(agentId)).map(conversationItem);
      set((s) => ({
        conversations: { ...s.conversations, [agentId]: items },
        conversationLoad: { ...s.conversationLoad, [agentId]: 'ready' },
      }));
    } catch (err) {
      console.error('Failed to load conversations:', err);
      set((s) =>
        s.conversationLoad[agentId] === 'ready' ? s : { conversationLoad: { ...s.conversationLoad, [agentId]: 'failed' } },
      );
    }
  },

  refreshLoaded: async () => {
    const { treeLoad, conversationLoad, refreshFiles, refreshConversations } = get();
    await Promise.all([
      ...Object.keys(treeLoad).filter((id) => treeLoad[id] === 'ready').map(refreshFiles),
      ...Object.keys(conversationLoad).filter((id) => conversationLoad[id] === 'ready').map(refreshConversations),
    ]);
  },

  setTree: (agentId, update) =>
    set((s) => {
      const items = update(s.trees[agentId] ?? []);
      return items === s.trees[agentId] ? s : { trees: { ...s.trees, [agentId]: items } };
    }),

  setConversations: (agentId, update) =>
    set((s) => {
      const items = update(s.conversations[agentId] ?? []);
      return items === s.conversations[agentId] ? s : { conversations: { ...s.conversations, [agentId]: items } };
    }),

  reset: () => {
    seeded.clear();
    set({ trees: {}, conversations: {}, treeLoad: {}, conversationLoad: {} });
  },
}));

const EMPTY: TreeItem[] = [];

/** The agent's tree, loading it on first use. `loaded` is false until it has
 *  answered — until then the list says nothing about what exists. */
export function useAgentTree(agentId: string | null | undefined): { items: TreeItem[]; loaded: boolean } {
  const items = useAgentTreeStore((s) => (agentId ? s.trees[agentId] : undefined)) ?? EMPTY;
  const loaded = useAgentTreeStore((s) => !!agentId && s.treeLoad[agentId] === 'ready');
  useEnsure(agentId);
  return { items, loaded };
}

/** My conversations with the agent, newest first, loading them on first use. */
export function useAgentConversations(agentId: string | null | undefined): { items: TreeItem[]; loaded: boolean } {
  const items = useAgentTreeStore((s) => (agentId ? s.conversations[agentId] : undefined)) ?? EMPTY;
  const loaded = useAgentTreeStore((s) => !!agentId && s.conversationLoad[agentId] === 'ready');
  useEnsure(agentId);
  return { items, loaded };
}

function useEnsure(agentId: string | null | undefined) {
  useEffect(() => {
    if (agentId) useAgentTreeStore.getState().ensure(agentId);
  }, [agentId]);
}

/** Where a tree item is held: its agent, and whether it is a conversation. */
export function locateItem(itemId: string): { agentId: string; item: TreeItem; conversation: boolean } | null {
  const { trees, conversations } = useAgentTreeStore.getState();
  for (const [agentId, items] of Object.entries(conversations)) {
    const item = items.find((i) => i.id === itemId);
    if (item) return { agentId, item, conversation: true };
  }
  for (const [agentId, items] of Object.entries(trees)) {
    const item = findItemById(items, itemId);
    if (item) return { agentId, item, conversation: false };
  }
  return null;
}

export function findItemById(items: TreeItem[], id: string): TreeItem | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rows → tree items
// ─────────────────────────────────────────────────────────────────────────────

export function folderItem(folder: FolderRow): TreeItem {
  return {
    id: folder.id,
    name: folder.name,
    type: 'folder',
    parentId: folder.parent_folder_id,
    agentId: folder.agent_id,
    syncStatus: folder.sync_status && folder.sync_status !== 'none' ? (folder.sync_status as 'active' | 'error') : undefined,
    syncError: folder.sync_error ?? undefined,
    syncSkipped: folder.sync_skipped?.length ? folder.sync_skipped : undefined,
    lastSyncedAt: folder.last_synced_at ?? undefined,
    createdAt: folder.created_at || new Date().toISOString(),
    updatedAt: folder.updated_at || new Date().toISOString(),
  };
}

export function fileItem(file: FileRow): TreeItem {
  return {
    id: file.id,
    name: fileStem(file.filename),
    filename: file.filename,
    type: 'file',
    parentId: file.folder_id,
    agentId: file.agent_id,
    createdAt: file.created_at || new Date().toISOString(),
    updatedAt: file.updated_at || new Date().toISOString(),
    processing_status: file.processing_status ?? undefined,
    file_type: file.file_type,
    has_metadata: file.has_metadata ?? false,
    source: file.source ?? undefined,
    deliveredFrom: file.delivered_from
      ? { workId: file.delivered_from.work_id, version: file.delivered_from.version, path: file.delivered_from.path }
      : undefined,
  };
}

/** A conversation is never nested — folders organize files. */
export function conversationItem(conv: ConversationRow): TreeItem {
  return {
    id: conv.id,
    name: conv.name,
    type: 'conversation',
    parentId: null,
    routineRunId: conv.routine_run_id ?? null,
    agentId: conv.agent_id ?? null,
    createdAt: conv.created_at || new Date().toISOString(),
    updatedAt: conv.updated_at || new Date().toISOString(),
  };
}

function buildFileTree(tree: AgentFiles): TreeItem[] {
  return nest([...tree.folders.map(folderItem), ...tree.files.map(fileItem)]);
}

/** Flat items nested under their folders; an item whose folder is absent is a root. */
function nest(flatItems: TreeItem[]): TreeItem[] {
  const itemMap = new Map<string, TreeItem>();
  const rootItems: TreeItem[] = [];
  for (const item of flatItems) itemMap.set(item.id, { ...item, children: [] });
  for (const item of flatItems) {
    const node = itemMap.get(item.id)!;
    const parent = item.parentId ? itemMap.get(item.parentId) : undefined;
    if (parent) parent.children!.push(node);
    else rootItems.push(node);
  }
  return rootItems;
}

/** Files still being ingested, into the processing-updates store, so a reload
 *  picks their progress back up. */
function seedProcessing(items: TreeItem[]) {
  const files: Array<{ fileId: string; status: ProcessingStatus }> = [];
  const collect = (list: TreeItem[]) => {
    for (const item of list) {
      const ps = item.processing_status;
      if (item.type === 'file' && ps && ps.status === 'ingesting') {
        files.push({
          fileId: item.id,
          status: {
            status: ps.status,
            unitsTotal: ps.units_total,
            unitsIngested: ps.units_ingested,
            unitType: ps.unit_type,
            isQueryable: ps.is_queryable,
            error: ps.error,
          },
        });
      }
      if (item.children) collect(item.children);
    }
  };
  collect(items);
  if (files.length > 0) useFileStatusStore.getState().seedStatuses(files);
}

// The ingest decides a file's kind from its bytes; a row was created with the
// name's provisional kind. Follow the processing-updates stream's verdict so a
// row's icon and viewer are the file's, not the name's.
let lastFileTypes = useFileStatusStore.getState().fileTypes;
useFileStatusStore.subscribe((state) => {
  if (state.fileTypes === lastFileTypes) return;
  lastFileTypes = state.fileTypes;
  const fileTypes = state.fileTypes;
  const patch = (items: TreeItem[]): TreeItem[] => {
    let changed = false;
    const next = items.map((item) => {
      let out = item;
      if (item.type === 'file') {
        const ft = fileTypes[item.id] as TreeItem['file_type'] | undefined;
        if (ft && ft !== item.file_type) {
          out = { ...item, file_type: ft };
          changed = true;
        }
      }
      if (item.children) {
        const kids = patch(item.children);
        if (kids !== item.children) {
          out = { ...out, children: kids };
          changed = true;
        }
      }
      return out;
    });
    return changed ? next : items;
  };
  const { trees, setTree } = useAgentTreeStore.getState();
  for (const agentId of Object.keys(trees)) setTree(agentId, patch);
});
